import React, { useEffect, useRef, useMemo } from 'react';
import { useLeaflet } from './useLeaflet';
import { planLabels, type PlanItem, type Placement } from './labelPlan';
import { structureOf, lensClaimsTract } from '../data/structure';
import { toLatLngs, centerLatLng, boundsOf, rdp, computeAutoRotation, getViewRotationDeg } from '../data/geometry';
import { LENSES, LensId, colorFor, catColor, orderRamp, rampAt, orderBandOf } from '../data/lenses';
import type { Parcel } from '../data/useParcels';
import { T } from '../theme/tokens';
import { prefersReducedMotion } from '../util/anim';

interface Props {
  parcels: Parcel[];
  lens: LensId;
  /** ordered Split-by layer stack — one tooltip decode line per level */
  splitLenses?: LensId[];
  selectedId: string | null;
  filteredOut: Set<string>;
  isolated: Set<string>;
  flyToId: string | null;
  flyNonce: number;
  fitNonce: number;
  tilt: boolean;
  cb: boolean;
  showMarkers: boolean;
  hovered: string | null;
  /** split-tree hover: [primaryCat, level1Cat, level2Cat?] — spotlight the INTERSECTION */
  hoveredPath?: string[] | null;
  hoveredLotIds?: string[] | null;  // precise panel-computed spotlight set (sub-row hovers)
  pinnedLotIds?: string[] | null;   // PERSISTENT spotlight — a clicked breakdown sub-value stays lit
  hoveredLotId?: string | null;
  present?: boolean;
  /** show the on-map legend — true whenever the AnalyticsPanel (the usual decode key) is hidden:
   *  Present mode AND lot-selected (detail panel open). The map must never be undecodable. */
  legendVisible?: boolean;
  /** Auto-density chrome zoom (SiteMapApp). Applied ONLY to the legend overlay — a plain DOM
   *  sibling of the Leaflet container. NEVER zoom the Leaflet container itself (elRef) or
   *  anything Leaflet manages: its pointer/coordinate math requires a 1:1 unscaled pane. */
  uiScale?: number;
  onSelect: (id: string) => void;
  onToggleIsolate: (cat: string) => void;
}

interface Target { color: string; fillColor: string; weight: number; opacity: number; fillOpacity: number; }

function MapCanvas({ parcels, lens, splitLenses, selectedId, filteredOut, isolated, flyToId, flyNonce, fitNonce, tilt, cb, showMarkers, hovered, hoveredPath, hoveredLotIds, pinnedLotIds, hoveredLotId, present, legendVisible, uiScale = 1, onSelect, onToggleIsolate }: Props) {
  const { ready, failed } = useLeaflet();
  const elRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const layersRef = useRef<Map<string, any>>(new Map());
  const curRef = useRef<Map<string, Target>>(new Map());
  // Static per-polygon geometry (bbox center + NE/NW corners), cached once at build so the
  // per-frame label loop doesn't call getBounds() for all ~467 polygons every animation frame.
  const geomRef = useRef<Map<string, { c: any; ne: any; nw: any; sw: any }>>(new Map());
  const labelLayerRef = useRef<any>(null);
  const labelMarkersRef = useRef<Map<string, { m: any; key: string }>>(new Map()); // id → marker, diffed
  const critLayerRef = useRef<any>(null);
  const critMarkersRef = useRef<Map<string, any>>(new Map()); // id → pulse marker, for diff-update
  const builtRef = useRef(false);
  const baseZoomRef = useRef<number>(0);
  const rafRef = useRef<number | undefined>(undefined); // React 19: useRef requires an initial arg
  const hoverBadgeRef = useRef<any>(null);
  const roRef = useRef<any>(null);
  const fittedRef = useRef(false);
  const dataRef = useRef<{ parcels: Parcel[]; filteredOut: Set<string>; selectedId: string | null; lens: LensId; isolated: Set<string>; orderBandById: Map<string, string> | null }>({ parcels, filteredOut, selectedId, lens, isolated, orderBandById: null });
  useEffect(() => { dataRef.current = { parcels, filteredOut, selectedId, lens, isolated, orderBandById }; });
  // Pinned-spotlight set as a REF so applyStyles always reads the CURRENT pin even when called from
  // a stale-closure effect (a live data refresh re-applying base styles must NOT wipe the pin).
  const pinRef = useRef<string[] | null>(pinnedLotIds ?? null);
  pinRef.current = pinnedLotIds ?? null;

  // Auto-orient the plat to fill the canvas. Runs in render (via useMemo) so the rotation
  // transform is set before any layout effect reads geometry (build / fit / labels).
  useMemo(() => computeAutoRotation(parcels), [parcels]);
  const northDeg = -getViewRotationDeg(); // re-point the compass to true north after rotation

  // Build Order heat re-normalizes to the VISIBLE set: rank 1 of the current filter = darkest,
  // last visible rank = brightest — the full gradient always spans whatever is on screen,
  // instead of freezing to global quintiles (a filtered phase showed as one flat colour).
  const orderT = useMemo(() => {
    if (lens !== 'buildOrder') return null;
    const vis = parcels.filter(p => !filteredOut.has(p.id) && p.buildOrder != null)
      .sort((a, b) => (a.buildOrder as number) - (b.buildOrder as number));
    const m = new Map<string, number>();
    vis.forEach((p, i) => m.set(p.id, vis.length > 1 ? i / (vis.length - 1) : 0.5));
    return m;
  }, [lens, parcels, filteredOut]);
  // Per-lot VIEW-RELATIVE Build-Order band (quintile of the same orderT used for the heat). Drives
  // isolation dimming + label visibility + hover spotlight so clicking a breakdown band lights EXACTLY
  // the lots that band shows (and the same lots the heat shades). null unless Build Order is primary.
  const orderBandById = useMemo(() => {
    if (!orderT) return null;
    const m = new Map<string, string>();
    orderT.forEach((t, id) => m.set(id, orderBandOf(t)));
    return m;
  }, [orderT]);

  // On-map legend for the active lens — counts over the non-filtered set, click to isolate.
  // Multi-value lenses (Model / Sales Incentives / Construction Upgrades) tokenize the
  // comma-joined value (split, trim, dedupe per parcel — mirrors aggregate.ts) so each legend
  // row is ONE token: clicking isolates that token (inIsolation is token-aware) and the swatch
  // matches catColor's first-token fill.
  const legend = useMemo(() => {
    const def = LENSES[lens];
    const counts = new Map<string, number>();
    for (const p of parcels) {
      if (filteredOut.has(p.id)) continue;
      const raw = def.cat(p);
      const keys = def.multi && raw && raw !== '—'
        ? Array.from(new Set(raw.split(',').map(s => s.trim()).filter(Boolean)))
        : [raw];
      for (const k of keys) counts.set(k, (counts.get(k) || 0) + 1);
    }
    const rest = [...counts.keys()].filter(k => !def.order.includes(k)).sort((a, b) => counts.get(b)! - counts.get(a)!);
    const ordered = [...def.order.filter(k => counts.has(k)), ...rest];
    const all = ordered.map(k => ({ k, n: counts.get(k)!, c: catColor(def, k) }));
    return { label: def.label, rows: all.slice(0, 22), more: Math.max(0, all.length - 22) };
  }, [parcels, filteredOut, lens, cb]);

  // init
  useEffect(() => {
    if (!ready || !elRef.current || mapRef.current) return;
    const L = window.L;
    const map = L.map(elRef.current, {
      crs: L.CRS.Simple, preferCanvas: true, minZoom: -6, maxZoom: 6,
      zoomControl: true, attributionControl: false, zoomSnap: 0.25, fadeAnimation: false,
    });
    // Custom scale bar: Leaflet's scale control assumes map.distance() returns METERS, but under
    // CRS.Simple it returns raw coordinate units — ours are PLANAR FEET (geometry.ts). The stock
    // imperial control therefore overstated distances ~3.28×. This one reads feet directly.
    const ScaleFt = L.Control.extend({
      options: { position: 'bottomleft' },
      onAdd(m: any) {
        const el = L.DomUtil.create('div', 'lfe-scaleft');
        el.style.cssText = `background:${T.paper}d9;border:1px solid ${T.line};border-top:none;` +
          `font:11px/1.4 "IBM Plex Mono",monospace;padding:0 6px;color:${T.ink};border-radius:0 0 4px 4px;` +
          `border-bottom:2px solid ${T.inkSoft};white-space:nowrap;`;
        const update = () => {
          // The map has NO view until the first fitBounds (init runs before layout settles in the
          // Airtable iframe) — projecting before then makes Leaflet throw and crash the element.
          if (!m._loaded) return;
          try {
            const c = m.getSize();
            const a = m.containerPointToLatLng([c.x / 2 - 50, c.y / 2]);
            const b = m.containerPointToLatLng([c.x / 2 + 50, c.y / 2]);
            const ftPer100px = Math.hypot(b.lng - a.lng, b.lat - a.lat); // CRS.Simple: latlng IS feet
            if (!isFinite(ftPer100px) || ftPer100px <= 0) return;
            // pick a round number ≤ the feet that fit in ~100px
            const nice = [1, 2, 5].flatMap(d => [d, d * 10, d * 100, d * 1000, d * 10000]).sort((x, y) => x - y);
            const target = ftPer100px; let ft = nice[0];
            for (const v of nice) { if (v <= target) ft = v; else break; }
            el.style.width = `${Math.round((ft / ftPer100px) * 100)}px`;
            el.textContent = ft >= 5280 ? `${(ft / 5280).toFixed(ft % 5280 ? 1 : 0)} mi` : `${ft.toLocaleString('en-US')} ft`;
          } catch { /* view not ready yet — the next move/zoom event will retry */ }
        };
        m.on('zoomend zoom move viewreset', update); update();
        (this as any)._update = update;
        return el;
      },
    });
    map.addControl(new ScaleFt());
    labelLayerRef.current = L.layerGroup().addTo(map);
    critLayerRef.current = L.layerGroup().addTo(map);
    let labelRaf = 0;
    const throttledLabels = () => { if (labelRaf) return; labelRaf = requestAnimationFrame(() => { labelRaf = 0; updateLabels(); }); };
    map.on('zoomend moveend', throttledLabels);
    mapRef.current = map;

    // CRITICAL (Airtable iframe): the grid cell often has no measured size when L.map()
    // runs, so fitBounds computes against a 0×0 viewport and nothing is visible. Watch the
    // container; invalidateSize on every resize, and fit once it actually has dimensions.
    const fitNow = () => {
      const m = mapRef.current; if (!m) return;
      m.invalidateSize({ animate: false });
      const ps = dataRef.current.parcels;
      const el = elRef.current;
      const w = el ? el.clientWidth : 0, h = el ? el.clientHeight : 0;
      if (builtRef.current && ps.length && w > 0 && h > 0) {
        try { m.fitBounds(boundsOf(ps), { padding: [28, 28] }); baseZoomRef.current = m.getZoom(); fittedRef.current = true; updateLabels(); } catch { /* noop */ }
      }
    };
    const ro = new ResizeObserver(() => fitNow());
    ro.observe(elRef.current);
    roRef.current = ro;
    // belt-and-suspenders for late iframe layout
    requestAnimationFrame(fitNow);
    const t1 = window.setTimeout(fitNow, 120);
    const t2 = window.setTimeout(fitNow, 400);

    return () => {
      cancelAnimationFrame(rafRef.current!); window.clearTimeout(t1); window.clearTimeout(t2);
      if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
      map.remove(); mapRef.current = null; builtRef.current = false; fittedRef.current = false;
      layersRef.current.clear(); curRef.current.clear(); geomRef.current.clear(); critMarkersRef.current.clear(); labelMarkersRef.current.clear();
    };
  }, [ready]);

  // build once
  useEffect(() => {
    const map = mapRef.current; if (!map || !window.L || builtRef.current || !parcels.length) return;
    const L = window.L;
    const renderer = L.canvas({ padding: 0.5 });
    const group = L.layerGroup().addTo(map);
    for (const p of parcels) {
      const t = targetFor(p, lens, selectedId, filteredOut, isolated, orderT, orderBandById);
      // Light Douglas–Peucker (¾-ft tolerance) strips the tracer's sub-foot vertex noise:
      // ~same shape, far fewer points → crisper edges and a lighter canvas at 1,045 polygons.
      // Donut/multipart tracts (Park Tract 3): outer ring first, then interior HOLES (cut out),
      // then any additional OUTER parts as a MultiPolygon — so the footprint isn't drawn ~2x.
      const simp = (r: any) => toLatLngs(rdp(r, 0.75));
      const outer = [simp(p.geo.ring), ...p.geo.holes.map(simp)];           // ring 0 + holes
      const latlngs = p.geo.parts.length ? [outer, ...p.geo.parts.map(pt => [simp(pt)])] : outer;
      const poly = L.polygon(latlngs, { renderer, ...t });
      poly._pid = p.id;
      poly.on('click', () => onSelect(p.id));
      poly.on('mouseover', function (this: any) { if (p.id !== selectedId) this.setStyle({ weight: 1.8 }); });
      poly.on('mouseout', function (this: any) { if (p.id !== selectedId) this.setStyle({ weight: curRef.current.get(p.id)?.weight ?? 1 }); });
      poly.bindTooltip(tipHtml(p, lens, splitLenses), { sticky: true, direction: 'top', opacity: 1, className: 'lfe-tip', offset: [0, -4] });
      group.addLayer(poly);
      layersRef.current.set(p.id, poly);
      curRef.current.set(p.id, t);
      const b = poly.getBounds(); geomRef.current.set(p.id, { c: b.getCenter(), ne: b.getNorthEast(), nw: b.getNorthWest(), sw: b.getSouthWest() });
    }
    builtRef.current = true;
    map.invalidateSize({ animate: false });
    const el = elRef.current;
    // Fit ONLY on the first successful build. This effect also re-runs on every live data re-sync
    // (useRecords streams a new `parcels` reference in seconds after load); re-fitting then would
    // yank the view back out to all-lots a few seconds after the user clicked/zoomed a lot. After the
    // initial fit we rebuild polygons but leave the camera where the user left it. A deliberate
    // "fit to view" still works via the user-triggered fitNonce effect below.
    if (!fittedRef.current && el && el.clientWidth > 0 && el.clientHeight > 0) {
      map.fitBounds(boundsOf(parcels), { padding: [28, 28] });
      baseZoomRef.current = map.getZoom();
      fittedRef.current = true;
    } // else: ResizeObserver/timeouts in the init effect will fit once the iframe lays out
    updateLabels();
  }, [parcels]); // eslint-disable-line

  // restyle (animated) on lens / selection / filter / isolate
  useEffect(() => {
    if (!builtRef.current) return;
    applyStyles(!prefersReducedMotion() && parcels.length <= 1400);
    updateLabels();
  }, [lens, selectedId, filteredOut, isolated, cb, pinnedLotIds]); // eslint-disable-line

  // Tooltips were bound once at build with that moment's data + lens → they went stale AND were
  // lens-blind. Re-bind content whenever the lens or the live records change (rare; cheap).
  useEffect(() => {
    if (!builtRef.current) return;
    for (const p of parcels) layersRef.current.get(p.id)?.setTooltipContent(tipHtml(p, lens, splitLenses));
  }, [lens, parcels, splitLenses]);

  // Linked highlighting — spotlight lots matching the hovered analytics/legend category (snappy, no tween).
  useEffect(() => {
    if (!builtRef.current) return;
    // HOVER-only transient spotlight. The PERSISTENT pin is handled inside applyStyles (so it
    // survives data refreshes), so when there's no active hover we just restyle — applyStyles
    // re-applies the base + the current pin.
    const idSet = (hoveredLotIds && hoveredLotIds.length) ? new Set(hoveredLotIds) : null;
    const path = idSet ? null : ((hoveredPath && hoveredPath.length) ? hoveredPath : (hovered ? [hovered] : null));
    if (!idSet && !path) { applyStyles(false); return; }
    // path[0] = primary lens; deeper entries = active split lenses in order — sub-row hovers
    // spotlight the INTERSECTION ("Sold Emeralds in Phase 1"). Token-aware for multi lenses.
    const defs = [LENSES[lens], ...(splitLenses || []).map(s => LENSES[s])];
    const catHas = (def: any, p: Parcel, key: string) =>
      // Build Order (primary) hovers by its VIEW-RELATIVE band — same source as the heat/dim/breakdown.
      def.id === 'buildOrder' && orderBandById ? (orderBandById.get(p.id) ?? 'No build order') === key
      : def.multi ? String(def.cat(p)).split(',').some((t: string) => t.trim() === key) : def.cat(p) === key;
    for (const p of parcels) {
      const poly = layersRef.current.get(p.id); if (!poly) continue;
      // Spotlight composes with — never overrides — selection (the selected lot keeps its ring).
      if (p.id === selectedId) continue;
      const match = !filteredOut.has(p.id) && (idSet ? idSet.has(p.id) : path!.every((key, i) => defs[i] && catHas(defs[i], p, key)));
      // Basemap structures dim HARD when they don't match — skipping them left wetlands/roads at
      // full brightness while every lot dimmed, which read as the wrong things being highlighted.
      // They still match (hovering "Wetland Preserve" lights the wetlands) at tract intensity.
      const isLot = structureOf(p) === 'lot';
      const st = match
        ? { fillOpacity: isLot ? 0.88 : 0.7, opacity: 1, weight: isLot ? 1.6 : 1.2 }
        : { fillOpacity: isLot ? 0.12 : 0.05, opacity: isLot ? 0.3 : 0.16, weight: 0.5 };
      poly.setStyle(st);
      // Keep curRef a faithful mirror of the *applied* style so applyStyles' diff (below) can
      // safely skip unchanged polygons without leaving any stuck in this spotlight state.
      const cur = curRef.current.get(p.id); if (cur) curRef.current.set(p.id, { ...cur, ...st });
    }
    // filteredOut/lens/splitLenses/selectedId are READ above (dim non-matching, resolve the
    // category path, skip the selected lot) — without them in deps, changing a filter or the
    // Color-By mid-hover recomputed the spotlight against stale state (it silently dropped).
  }, [hovered, hoveredPath, hoveredLotIds, filteredOut, lens, splitLenses, selectedId]); // eslint-disable-line

  // Hovered dock/search row → drop a green badge on that lot (mirrors the selected-lot badge) and
  // lift its polygon, so scanning rows in the bottom panel spotlights the lot on the map.
  useEffect(() => {
    const map = mapRef.current, L = window.L; if (!map || !L) return;
    if (hoverBadgeRef.current) { map.removeLayer(hoverBadgeRef.current); hoverBadgeRef.current = null; }
    if (!hoveredLotId || hoveredLotId === selectedId) return; // selected already shows its own badge
    const p = parcels.find(x => x.id === hoveredLotId); if (!p) return;
    const poly = layersRef.current.get(p.id);
    const ll = geomRef.current.get(p.id)?.c || (poly ? poly.getBounds().getCenter() : L.latLng(centerLatLng(p.geo)));
    hoverBadgeRef.current = L.marker(ll, { interactive: false, zIndexOffset: 2000,
      // Distinct dashed-outline chip — must NOT impersonate the solid selection badge.
      icon: L.divIcon({ className: 'lfe-label lfe-label-sel lfe-label-hover', html: labelText(p, dataRef.current.lens, 15), iconSize: undefined as any }) }).addTo(map);
    if (poly) { poly.bringToFront(); poly.setStyle({ weight: 2.2 }); }
    return () => {
      if (hoverBadgeRef.current) { try { map.removeLayer(hoverBadgeRef.current); } catch { /* map gone */ } hoverBadgeRef.current = null; }
      if (poly) poly.setStyle({ weight: curRef.current.get(p.id)?.weight ?? 1 });
    };
  }, [hoveredLotId, selectedId, parcels]); // eslint-disable-line

  function applyStyles(animate: boolean) {
    cancelAnimationFrame(rafRef.current!);
    const targets = new Map<string, Target>();
    for (const p of parcels) targets.set(p.id, targetFor(p, lens, selectedId, filteredOut, isolated, orderT, orderBandById));
    // PERSISTENT pin: a clicked breakdown sub-value keeps its lots spotlit across EVERY restyle
    // (live data refresh, lens/filter change). Override each parcel's target opacity/weight here so
    // it's part of the base render, not a transient overlay that the next applyStyles wipes.
    const pin = pinRef.current;
    if (pin && pin.length) {
      const pinSet = new Set(pin);
      for (const p of parcels) {
        if (p.id === selectedId) continue;            // selection keeps its ring
        const base = targets.get(p.id); if (!base) continue;
        const isLot = structureOf(p) === 'lot';
        const lit = !filteredOut.has(p.id) && pinSet.has(p.id);
        targets.set(p.id, lit
          ? { ...base, fillOpacity: isLot ? 0.88 : 0.7, opacity: 1, weight: isLot ? 1.6 : 1.2 }
          : { ...base, fillOpacity: isLot ? 0.12 : 0.05, opacity: isLot ? 0.3 : 0.16, weight: 0.5 });
      }
    }
    if (!animate) {
      for (const [id, t] of targets) { layersRef.current.get(id)?.setStyle(t); curRef.current.set(id, t); }
      bringSelectedFront(); return;
    }
    // Animate only the polygons whose target actually changed vs the current style. Selecting one
    // lot changes ~2 polygons; without this we tweened all ~467 every frame (most to an identical
    // colour). curRef is kept faithful (incl. the hover spotlight above), so skipping is safe.
    const same = (a: Target, b: Target) => a.color === b.color && a.fillColor === b.fillColor
      && a.opacity === b.opacity && a.fillOpacity === b.fillOpacity && a.weight === b.weight;
    const changed: { a: Target; b: Target; poly: any }[] = [];
    for (const [id, b] of targets) {
      const poly = layersRef.current.get(id); if (!poly) continue;
      const a = curRef.current.get(id);
      if (a && same(a, b)) continue;
      changed.push({ a: a || b, b, poly });
    }
    if (!changed.length) { for (const [id, t] of targets) curRef.current.set(id, t); bringSelectedFront(); return; }
    const start = performance.now(), dur = 240;
    const tick = (now: number) => {
      const k = Math.min(1, (now - start) / dur), e = 1 - Math.pow(1 - k, 3);
      for (const c of changed) {
        const a = c.a, b = c.b;
        c.poly.setStyle({
          color: lerpHex(a.color, b.color, e), fillColor: lerpHex(a.fillColor, b.fillColor, e),
          opacity: a.opacity + (b.opacity - a.opacity) * e, fillOpacity: a.fillOpacity + (b.fillOpacity - a.fillOpacity) * e,
          weight: b.weight,
        });
      }
      if (k < 1) rafRef.current = requestAnimationFrame(tick);
      else { for (const [id, t] of targets) curRef.current.set(id, t); bringSelectedFront(); }
    };
    rafRef.current = requestAnimationFrame(tick);
  }

  function bringSelectedFront() { if (selectedId) layersRef.current.get(selectedId)?.bringToFront(); }

  // fly / fit
  useEffect(() => { const m = mapRef.current; if (!m || !flyToId) return; const poly = layersRef.current.get(flyToId); if (poly) m.flyToBounds(poly.getBounds().pad(3), { maxZoom: 4, duration: prefersReducedMotion() ? 0 : 0.7, easeLinearity: 0.22 }); }, [flyToId, flyNonce]);

  // Always-on critical-lot pulse markers — closeout risk stays visible under any lens. Diff against
  // the previously-rendered set so a filter edit only adds/removes the changed markers instead of
  // clearing + recreating every divIcon (filtering is the hot path).
  useEffect(() => {
    const L = window.L, layer = critLayerRef.current; if (!L || !layer) return;
    const markers = critMarkersRef.current;
    const want = new Set<string>();
    if (showMarkers) for (const p of parcels) { if (p.urgencyRank === 1 && !filteredOut.has(p.id)) want.add(p.id); }
    for (const [id, m] of markers) { if (!want.has(id)) { layer.removeLayer(m); markers.delete(id); } }
    for (const p of parcels) {
      if (!want.has(p.id) || markers.has(p.id)) continue;
      const g = geomRef.current.get(p.id);
      const m = L.marker(g ? g.c : L.latLng(centerLatLng(p.geo)), {
        interactive: false, keyboard: false,
        icon: L.divIcon({ className: '', html: '<div class="lfe-crit-dot"></div>', iconSize: [9, 9], iconAnchor: [4, 4] }),
      }).addTo(layer);
      markers.set(p.id, m);
    }
  }, [parcels, filteredOut, showMarkers]);
  useEffect(() => { const m = mapRef.current; if (!m || !builtRef.current || !fitNonce) return; m.invalidateSize({ animate: false }); m.fitBounds(boundsOf(parcels), { padding: [28, 28] }); }, [fitNonce]); // eslint-disable-line

  // tilt → presentation mode (disable interaction while tilted)
  useEffect(() => {
    const m = mapRef.current; if (!m) return;
    const h = ['dragging', 'scrollWheelZoom', 'doubleClickZoom', 'boxZoom', 'keyboard', 'touchZoom'];
    h.forEach(k => { if (m[k]) tilt ? m[k].disable() : m[k].enable(); });
  }, [tilt]);

  function updateLabels() {
    const map = mapRef.current, L = window.L; if (!map || !L || !labelLayerRef.current) return;
    // No view yet (fitBounds hasn't run — late iframe layout): projecting would throw and take
    // down the whole element. The ResizeObserver's fitNow() re-calls us once the map is loaded.
    if (!map._loaded) return;
    const { parcels, filteredOut, selectedId, lens: curLens, isolated, orderBandById: curBandById } = dataRef.current;
    const layer = labelLayerRef.current;

    // 1) Project candidates to screen space (bbox center + on-screen size, cached corners from
    //    build time — latLngToContainerPoint is pure math, no layout reads).
    const size = map.getSize();
    const items: PlanItem[] = [];
    for (const p of parcels) {
      const sel = p.id === selectedId;
      if (!sel && (filteredOut.has(p.id) || !inIsolation(p, curLens, isolated, curBandById))) continue;
      const g = geomRef.current.get(p.id);
      const ll = g ? g.c : L.latLng(centerLatLng(p.geo));
      const pt = map.latLngToContainerPoint(ll);
      let w = 0, h = 0;
      if (g) {
        w = Math.abs(map.latLngToContainerPoint(g.ne).x - map.latLngToContainerPoint(g.nw).x);
        h = Math.abs(map.latLngToContainerPoint(g.nw).y - map.latLngToContainerPoint(g.sw).y);
      }
      items.push({ id: p.id, x: pt.x, y: pt.y, w, h, name: p.name, block: p.block, lot: p.lot, buildOrder: p.buildOrder, kind: structureOf(p) });
    }

    // 2) Pure plan: every lot whose number fits on-screen gets one, sized to its lot — no overlap
    //    by construction. (Build Order keeps its zoom-tiered overview inside the planner.)
    const placements = planLabels(items, {
      lens: curLens, tier: map.getZoom() - baseZoomRef.current,
      selectedId, viewW: size.x, viewH: size.y,
    });

    // 3) Diff against live markers — only add new / remove stale / swap changed icons. Pan/zoom
    //    typically touches a handful of markers instead of recreating ~1,000 divIcons per tick.
    const live = labelMarkersRef.current;
    const want = new Map<string, Placement>(placements.map(pl => [pl.id, pl]));
    for (const [id, rec] of live) {
      if (!want.has(id)) { layer.removeLayer(rec.m); live.delete(id); }
    }
    for (const pl of placements) {
      const key = pl.text + '|' + pl.fontPx + '|' + (pl.sel ? 1 : 0);
      const rec = live.get(pl.id);
      if (rec && rec.key === key) continue;
      const icon = L.divIcon({
        className: pl.sel ? 'lfe-label lfe-label-sel' : 'lfe-label',
        html: pl.sel ? esc(pl.text) : `<span style="font-size:${pl.fontPx}px">${esc(pl.text)}</span>`,
        iconSize: undefined as any,
      });
      if (rec) { rec.m.setIcon(icon); rec.key = key; }
      else {
        const g = geomRef.current.get(pl.id);
        if (!g) continue; // lot added after build (no polygon yet) — px coords are NOT world coords
        const m = L.marker(g.c, { interactive: false, icon }).addTo(layer);
        live.set(pl.id, { m, key });
      }
    }
  }

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: T.sand2 }}>
      <div ref={elRef} className="anim" style={{
        position: 'absolute', inset: 0, background: T.sand2,
        transform: tilt ? 'perspective(1500px) rotateX(28deg) scale(1.04)' : 'none',
        transformOrigin: 'center 64%', transition: prefersReducedMotion() ? 'none' : 'transform .4s ' + T.ease,
      }} />
      <div className="lfe-vignette" />
      {tilt && <>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(to top, rgba(28,38,32,.16), rgba(28,38,32,0) 42%)' }} />
        <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 600, background: T.ink, color: T.onInk,
          fontSize: 11, fontWeight: 600, padding: '4px 9px', borderRadius: 7, letterSpacing: '.04em' }}>2.5D VIEW · pan/zoom paused</div>
      </>}
      {failed && (
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center', padding: 24 }}>
          <div>
            <div className="head" style={{ fontSize: 16, color: T.amber }}>Map library blocked</div>
            <div style={{ fontSize: 12.5, color: T.inkSoft, marginTop: 6, maxWidth: 320, lineHeight: 1.5 }}>
              Leaflet failed to load from the CDN — the extension iframe may be blocking external scripts.
              Lots, filters, and analytics still work; the map needs Leaflet bundled or allowlisted.
            </div>
          </div>
        </div>
      )}

      {/* North indicator */}
      {!tilt && (
        <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 500, width: 30, height: 30, borderRadius: '50%',
          background: T.paper, border: `1px solid ${T.line}`, boxShadow: T.shadow, display: 'grid', placeItems: 'center',
          fontSize: 10, fontWeight: 700, color: T.ink, letterSpacing: '.02em' }}>
          <span style={{ display: 'grid', placeItems: 'center', lineHeight: 1 }}>
            <span style={{ fontSize: 9, color: T.amber, marginBottom: -1, transform: `rotate(${northDeg}deg)`, transition: prefersReducedMotion() ? 'none' : `transform .4s ${T.ease}` }}>▲</span>N
          </span>
        </div>
      )}

      {/* On-map legend — shown whenever the AnalyticsPanel is hidden (Present mode, or a lot is
          selected so the detail panel replaced it). The colors must always be decodable.
          zoom: uiScale = auto-density (safe: this overlay is a sibling of the Leaflet pane). */}
      {(present || legendVisible) && !failed && legend.rows.length > 0 && (
        <div className="lfe-scroll" style={{ zoom: uiScale, position: 'absolute', right: 12, bottom: 12, zIndex: 500, maxHeight: '46%', overflow: 'auto',
          background: T.paper, border: `1px solid ${T.line}`, borderRadius: 11, padding: '9px 11px 8px',
          boxShadow: T.shadow, backdropFilter: 'blur(6px)', minWidth: 150 }}>
          <div className="eyebrow" style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <span>{legend.label}</span>
            {isolated.size > 0 && (
              <button onClick={() => onToggleIsolate('__clear__')} className="lfe-press"
                style={{ fontSize: 9.5, color: T.accent, background: 'none', border: 'none', cursor: 'pointer', padding: 0, letterSpacing: 0, textTransform: 'none' }}>clear</button>
            )}
          </div>
          {LENSES[lens].kind === 'sequential' && (() => {
            const ramp = lens === 'buildOrder' ? orderRamp() : legend.rows.filter(r => !/no permit|no build order|^—$/i.test(r.k)).map(r => r.c);
            return ramp.length > 1 ? <div style={{ height: 6, borderRadius: 3, margin: '0 2px 7px', background: `linear-gradient(90deg, ${ramp.join(',')})` }} /> : null;
          })()}
          {/* buildOrder heat is FILTER-RELATIVE (orderT re-stretches per view) — global-quintile
              bucket rows would contradict the on-map colors, so the strip + caption is the key. */}
          {lens === 'buildOrder' && (
            <div className="num" style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 10, color: T.inkSoft, padding: '0 2px' }}>
              <span>first</span><span>→</span><span>last in view</span>
            </div>
          )}
          {lens !== 'buildOrder' && legend.rows.map(r => {
            const active = isolated.size === 0 || isolated.has(r.k);
            return (
              <button key={r.k} onClick={() => onToggleIsolate(r.k)} className="lfe-press"
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '3px 4px',
                  border: 'none', background: isolated.has(r.k) ? T.sand2 : 'transparent', borderRadius: 6, cursor: 'pointer',
                  opacity: active ? 1 : 0.4 }}>
                <span style={{ width: 11, height: 11, borderRadius: 3, flex: '0 0 auto', background: r.c, border: '1px solid rgba(0,0,0,.14)' }} />
                <span style={{ fontSize: 11.5, color: T.ink, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.k}</span>
                <span className="num" style={{ fontSize: 10.5, color: T.inkSoft }}>{r.n.toLocaleString('en-US')}</span>
              </button>
            );
          })}
          {lens !== 'buildOrder' && legend.more > 0 && <div style={{ fontSize: 10.5, color: T.inkSoft, padding: '4px 4px 0' }}>+{legend.more} more</div>}
        </div>
      )}
    </div>
  );
}

// structureOf moved to data/structure.ts — single classifier shared with the PDF export.

/**
 * Figure-ground: a designed basemap reads under every lens. Water = the lake (blue),
 * roads = light corridors, open space = soft green, buildable lots carry the lens data
 * on crisp paper-white hairlines. When the lens IS Land Use, categorical colors win for
 * all parcels (parity with the source map). Selected lot = gold-haloed, front, opaque.
 */
// Is this parcel part of the active isolation? For multi-value lenses (e.g. Construction Upgrades)
// a lot's category is a comma-joined list, so it matches if ANY of its tokens is isolated — not
// just an exact whole-string match. Used for BOTH polygon dimming and label visibility.
function inIsolation(p: Parcel, lensId: LensId, isolated: Set<string>, bandById?: Map<string, string> | null): boolean {
  if (isolated.size === 0) return true;
  // Build Order isolates by its VIEW-RELATIVE band (the same orderT quintile the heat + breakdown
  // use), not the global quintile on the parcel — so a band lights exactly the lots it shows.
  if (lensId === 'buildOrder' && bandById) return isolated.has(bandById.get(p.id) ?? 'No build order');
  const lens = LENSES[lensId];
  const cat = lens.cat(p);
  if (lens.multi) return cat.split(',').some(t => isolated.has(t.trim()));
  return isolated.has(cat);
}

function targetFor(p: Parcel, lensId: LensId, selectedId: string | null, filteredOut: Set<string>, isolated: Set<string>, orderT?: Map<string, number> | null, bandById?: Map<string, string> | null): Target {
  const lens = LENSES[lensId];
  const sel = p.id === selectedId;
  const cat = lens.cat(p);
  const dim = filteredOut.has(p.id) || !inIsolation(p, lensId, isolated, bandById);
  const s = structureOf(p);
  // The lens claims a tract when it genuinely categorizes it — Land Use always; phase lenses
  // when the tract HAS a phase (a wetland in Phase 2 colours like Phase 2, per Eric 2026-06-11).
  // The tract keeps its structure stroke (water/park line) so it still reads as a tract.
  const claims = lensId === 'landUse' || lensClaimsTract(lensId, cat);

  let fill = (orderT && orderT.has(p.id)) ? rampAt(orderRamp(), orderT.get(p.id)!) : colorFor(lens, p), stroke = T.lotLine, weight = 0.8, fop = 0.74;
  if (s === 'water') { fill = claims ? colorFor(lens, p) : T.water; stroke = T.waterLine; weight = 1.2; fop = claims ? 0.74 : 0.78; }
  else if (s === 'road') { fill = claims ? colorFor(lens, p) : T.road; stroke = T.roadLine; weight = 0.9; fop = claims ? 0.74 : 0.52; }
  else if (s === 'open') { fill = claims ? colorFor(lens, p) : T.openSpace; stroke = T.openLine; weight = 0.8; fop = claims ? 0.74 : 0.6; }

  // Selection ring uses T.sel — a colour OUTSIDE every data palette (gold was also a category
  // fill: Phase 3 / Overdue / Requested, giving near-zero ring contrast on gold lots).
  if (sel) { stroke = T.sel, weight = 3.25, fop = 0.92; }
  // Dim floors: excluded LOTS stay readable as context (0.38/0.14), but excluded BASEMAP
  // structures (water/roads/open) dim much harder — their saturated hues (lake blue) otherwise
  // outshine the lots the user actually filtered for.
  const hardDim = dim && s !== 'lot';
  return { color: stroke, fillColor: fill, weight,
    opacity: hardDim ? 0.16 : dim ? 0.38 : (sel ? 1 : 0.9),
    fillOpacity: hardDim ? 0.05 : dim ? 0.14 : fop };
}

function esc(s: string) { return (s || '').replace(/[&<>]/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' } as any)[m]); }
function labelText(p: Parcel, lens?: LensId, maxChars = 15) {
  // Build Order lens: show the 1..N build sequence number on each lot.
  if (lens === 'buildOrder' && p.buildOrder != null) return String(p.buildOrder);
  // Prefer the concise lot number that reads like the plat; for named tracts show the FULL name
  // when it fits the lot (maxChars is derived from the lot's on-screen width), else trim.
  const n = (p.name || '').trim();
  const lead = n.match(/^(\d{1,4})\b/);
  const s = lead ? lead[1] : (n.length > maxChars ? n.slice(0, Math.max(2, maxChars - 1)) + '…' : n);
  return esc(s || (p.block != null && p.lot != null ? `${p.block}·${p.lot}` : ''));
}
function tipHtml(p: Parcel, lensId?: LensId, splitIds?: LensId[]) {
  const chips: string[] = [p.buildPhase];
  if (p.salesBucket && p.salesBucket !== 'N/A') chips.push(p.salesBucket);
  if (p.pctToCO != null) chips.push(Math.round(p.pctToCO <= 1 ? p.pctToCO * 100 : p.pctToCO) + '% CO');
  if (p.isException) chips.push('⚠ exception');
  const t = esc(p.name) + (p.model ? ' · ' + esc(p.model) : '');
  // Decode line: what the ACTIVE lens says about this lot — so hovering always explains the
  // colour you're looking at, under any lens (the tooltip used to be lens-blind).
  let lensLine = '';
  if (lensId) {
    const def = LENSES[lensId];
    // Build Order shows the exact 1..N rank (#142) — its global-quintile cat would contradict the
    // view-relative heat/bands/isolation, and the precise rank is more useful in a tooltip anyway.
    const v = lensId === 'buildOrder' ? (p.buildOrder != null ? '#' + p.buildOrder : 'No build order') : def.cat(p);
    if (v && v !== '—' && !chips.includes(v)) lensLine = `<div class="lfe-tip-s">${esc(def.label)}: <b>${esc(v)}</b></div>`;
  }
  // Split-by rides along on hover — the map fill stays single-lens, the tooltip carries the
  // extra dimensions, ONE LINE PER SPLIT LEVEL in stack order ("Model: Emerald" +
  // "Contract status: Escrow Received" + "Phase: Phase 1").
  let splitLines = '';
  for (const sid of splitIds || []) {
    if (sid === lensId) continue;
    const def = LENSES[sid];
    const v = def.cat(p);
    if (v && v !== '—') splitLines += `<div class="lfe-tip-s">${esc(def.label)}: <b>${esc(v)}</b></div>`;
  }
  return `<div class="lfe-tip-t">${t}</div><div class="lfe-tip-s">${chips.join(' · ')}</div>${lensLine}${splitLines}`;
}

function hexToRgb(h: string): [number, number, number] {
  let s = h.replace('#', ''); if (s.length === 3) s = s.split('').map(c => c + c).join('');
  const n = parseInt(s, 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function lerpHex(a: string, b: string, t: number): string {
  if (!a || a[0] !== '#') a = '#c9c2b2'; if (!b || b[0] !== '#') b = '#c9c2b2';
  const A = hexToRgb(a), B = hexToRgb(b);
  return `rgb(${Math.round(A[0] + (B[0] - A[0]) * t)},${Math.round(A[1] + (B[1] - A[1]) * t)},${Math.round(A[2] + (B[2] - A[2]) * t)})`;
}

// Memoized: the heaviest child. Props are now stable (parcels/filteredOut/isolated are
// memoized in SiteMapApp; onSelect/onToggleIsolate are useCallback'd) so unrelated app
// state (dock drag, export status, search query) no longer re-renders the whole map tree.
export default React.memo(MapCanvas);
